import { INTEGRATION_PARTNER } from '../../utils/enum';
import { EASY_MODE_PARTNER_LITIFY } from '../../constants/easyModeIntegrations';

// --- Shell ---------------------------------------------------------------

// leaves room for the fixed footer so the last field is never covered
export const shellClasses = 'pb-32';

// Layout only: the mocks run the hero straight onto the page, with the Stone
// step card as the sole panel, so neither wrapper carries a fill.
export const cardFrameClasses = 'mx-auto w-full max-w-[880px] p-6';

export const cardClasses = 'w-full px-6 py-14';

// one column width shared by the hero, the step card and the footer, so nothing shifts between steps
export const columnClasses = 'mx-auto w-full max-w-[560px]';

export const heroSubtitleClasses = 'mt-3! mb-0! text-sm leading-relaxed text-content';

export const heroEscapeHatchClasses = 'mt-4 flex items-center gap-1.5 text-sm text-content';

export const stepCardClasses = 'mt-8 rounded-lg bg-surface-alt p-5';

// --- Stepper -------------------------------------------------------------

export const stepperRowClasses = 'flex items-center gap-2';

export const stepperStepClasses = 'flex items-center gap-2';

export const stepperConnectorClasses = 'h-px w-4 shrink-0 bg-border-light';

// the icons draw with currentColor, so the state colors live here
export const stepperIconClasses = 'text-primary';

export const stepperIconInactiveClasses = 'text-border-accent';

export const stepperLabelClasses = 'whitespace-nowrap text-sm text-subtle';

export const stepperLabelActiveClasses = 'text-content';

export const stepperDividerClasses = 'mt-4 h-px w-full bg-border';

// --- Footer --------------------------------------------------------------

export const footerClasses =
  'fixed bottom-0 left-0 z-[3] w-full border-t border-border-light bg-surface py-4';

export const footerInnerClasses = 'flex items-center justify-between gap-4';

export const footerLabelClasses = 'text-sm text-subtle';

export const footerActionsClasses = 'flex items-center gap-3';

// --- Step 1 --------------------------------------------------------------

// the shared field labels are 16px app-wide; the mocks set them a step smaller inside this card only
export const stepBodyClasses = 'mt-5 [&_label]:text-sm';

export const requiredHintClasses = 'mb-4 text-sm text-subtle';

export const formGridClasses = 'grid grid-cols-1 gap-4 md:grid-cols-4';

export const fieldFullClasses = 'md:col-span-4';

export const fieldWideClasses = 'md:col-span-2';

export const fieldNarrowClasses = 'md:col-span-1';

export const fieldLabelClasses = 'block mb-2 text-content font-sans';

export const fieldErrorClasses = 'block mt-1 ml-1 text-sm text-danger';

export const dateInputClasses = `
  w-full h-10 px-3 py-1.5
  font-sans text-base text-content
  bg-white border border-border rounded-md
  transition-colors
  focus:outline-none focus:ring-2 focus:ring-primary-ring focus:border-primary
`;

// hides the native yyyy-mm-dd affordance so the mock's "Select Date" placeholder can show through
export const dateInputEmptyClasses = 'text-transparent';

export const datePlaceholderClasses =
  'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-subtle';

// --- Step 2 --------------------------------------------------------------

export const partnerPanelClasses = 'rounded-md bg-surface p-4';

export const partnerHeaderClasses = 'flex items-center justify-between gap-4';

export const partnerQuestionClasses = 'text-base text-subtle';

export const partnerSubtitleClasses = 'mt-2 text-sm text-subtle';

export const partnerButtonRowClasses = 'mt-4 grid grid-cols-2 gap-3 md:grid-cols-4';

export const partnerButtonBaseClasses = `
  flex h-10 items-center justify-center rounded-md
  px-2 text-sm font-semibold
  transition-opacity
  disabled:cursor-not-allowed disabled:opacity-50
`;

// Easy Mode's own partner treatment, kept apart from INTEGRATION_PARTNER_MAP.uiStyle so restyling
// here cannot change how Classic Intake's ImportPartnerSec looks.
export const EASY_MODE_PARTNER_BUTTON_CLASSES = {
  [INTEGRATION_PARTNER.CHARTSQUAD]: 'bg-white border border-border text-content',
  [INTEGRATION_PARTNER.CASEPEER]: 'bg-brand-casepeer border border-transparent text-white',
  [EASY_MODE_PARTNER_LITIFY]: 'bg-brand-litify border border-transparent text-white',
  [INTEGRATION_PARTNER.SMARTADVOCATE]:
    'bg-brand-smart-advocate border border-transparent text-white'
};

export const partnerCaseListClasses = 'mt-4 rounded-md border border-border';

export const partnerCaseListHeaderClasses =
  'flex items-center justify-between gap-4 border-b border-border px-3 py-2';

export const partnerCaseListTitleClasses = 'text-sm font-semibold text-heading';

export const partnerCaseListBodyClasses = 'max-h-64 overflow-y-auto';

export const partnerCaseRowClasses = `
  flex w-full items-center justify-between gap-3
  border-b border-border px-3 py-2 text-left text-sm text-content
  transition-colors last:border-b-0 hover:bg-surface-light
`;

export const partnerCaseNameClasses = 'truncate font-semibold';

export const partnerCaseDateClasses = 'shrink-0 text-xs text-subtle';

export const partnerCaseEmptyClasses = 'px-3 py-6 text-center text-sm text-subtle';

export const partnerCaseFooterClasses =
  'flex items-center justify-between gap-3 border-t border-border px-3 py-2 text-xs text-subtle';

export const importBannerClasses =
  'mt-4 flex items-start justify-between gap-4 rounded-md border border-warning-light bg-warning-surface p-3 text-sm text-content';

export const dropzoneClasses = `
  mt-4 flex flex-col items-center rounded-lg
  border border-dashed border-border bg-surface
  px-6 py-8 text-center transition-colors
`;

export const dropzoneActiveClasses = 'border-primary bg-primary-surface';

export const dropzoneIconClasses = 'text-primary';

export const dropzoneTitleClasses = 'mt-3 text-base font-semibold text-heading';

export const dropzoneSubtitleClasses = 'mt-1 text-sm text-subtle';

export const dropzoneDividerClasses = 'mt-5 flex w-full items-center gap-3';

export const dropzoneDividerLineClasses = 'h-px grow bg-border';

export const dropzoneDividerLabelClasses = 'text-xs uppercase tracking-wide text-subtle';

export const dropzoneButtonRowClasses = 'mt-4 grid w-full grid-cols-2 gap-3 md:grid-cols-4';

export const dropzoneButtonClasses = `
  flex h-10 items-center justify-center gap-2 rounded-md
  border border-border bg-surface
  px-2 text-xs font-semibold whitespace-nowrap text-primary
  transition-colors hover:border-primary
  disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border
`;

export const dropzoneErrorListClasses = 'mt-3 w-full text-left text-sm text-danger';

export const uploadListClasses = 'mt-4 overflow-hidden rounded-lg';

export const uploadListHeaderClasses =
  'flex items-center justify-between gap-4 bg-primary-hover px-4 py-2.5 text-sm font-semibold text-white';

export const uploadListBodyClasses = 'max-h-64 overflow-y-auto bg-surface px-4 py-3';

export const uploadRowClasses = 'flex items-center gap-2 py-1.5 text-sm text-content';

export const uploadRowNameClasses = 'truncate';

export const uploadRowFolderClasses = 'shrink-0 text-muted text-xs';

export const uploadRowTagClasses = 'shrink-0 text-subtle';

export const uploadRowTagWarningClasses = 'shrink-0 text-warning';

export const uploadRowStatusClasses = 'ml-auto flex shrink-0 items-center gap-2';

export const uploadRowActionClasses = 'text-sm font-semibold text-primary';

export const uploadRowRemoveClasses = 'text-subtle transition-colors hover:text-danger';

// --- Step 3 --------------------------------------------------------------

export const contextFieldClasses = 'resize-y! min-h-24';

export const contextFooterClasses = 'mt-1 flex items-start justify-between gap-3';

export const contextCounterClasses = 'ml-auto shrink-0 text-sm text-subtle';

export const contextCounterAtLimitClasses = 'ml-auto shrink-0 text-sm text-warning';

// worked examples that sit under the context field so the lawyer knows what to write
export const contextHelperClasses = 'mt-2 text-sm text-subtle';

export const contextExampleListClasses = 'mt-1 list-disc space-y-1 pl-5';

export const contextExampleLabelClasses = 'font-semibold text-content';

// optional defendant panel; mirrors the Step 2 partner-import panel so the two read identically
export const defendantPanelClasses = 'mt-4 rounded-md bg-surface p-4';

export const defendantHeaderClasses = 'flex items-center justify-between gap-4';

export const defendantQuestionClasses = 'text-base text-subtle';

export const defendantSubtitleClasses = 'mt-2 text-sm text-subtle';

export const defendantFieldGridClasses = 'mt-3 grid grid-cols-1 gap-4 md:grid-cols-2';

// each defendant sits in its own block; the first carries no divider so the list reads as one panel
export const defendantEntryClasses = 'mt-4 border-t border-border pt-4 first:mt-0 first:border-t-0 first:pt-0';

export const defendantEntryHeaderClasses = 'flex items-center justify-between gap-3';

export const defendantEntryTitleClasses = 'text-sm font-semibold text-content';

export const defendantAddRowClasses = 'mt-4';

export const defendantCapHintClasses = 'mt-3 text-sm text-subtle';
